import type { RouteDef, RouteResponse } from '@geo-admin/shared/contract/route.ts';
import {
  type QueryKey,
  type UseQueryResult,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { Realm } from '@geo-admin/shared/contract/session.ts';
import { type CallInput, call } from './call.ts';
import { realmOfPath } from './paths.ts';

/**
 * 캐시 키 맨 앞에 영역을 둔다 — 운영자·고객 로그인을 함께 가진 브라우저에서 영역을 건너도 내 계정(me)이 섞이지 않는다
 */
export function keyOf(
  route: RouteDef,
  input?: unknown,
  realm: Realm = realmOfPath(window.location.pathname),
): QueryKey {
  return [realm, route.method, route.path, input ?? null];
}

/** 조회 계약 → useQuery */
export function useApi<R extends RouteDef>(
  route: R,
  input: CallInput<R> = {},
  opts: { enabled?: boolean; refetchInterval?: number } = {},
) {
  return useQuery<RouteResponse<R>>({
    queryKey: keyOf(route, input),
    queryFn: () => call(route, input),
    enabled: opts.enabled,
    refetchInterval: opts.refetchInterval,
  });
}

/**
 * 같은 계약을 여러 입력으로 한꺼번에 — 키 규칙(영역 접두)은 useApi 와 같아서 캐시를 나눠 쓴다.
 * 입력 배열의 길이가 바뀌어도 훅은 하나다(useQueries).
 */
export function useApiList<R extends RouteDef>(route: R, inputs: CallInput<R>[]) {
  return useQueries({
    queries: inputs.map((input) => ({
      queryKey: keyOf(route, input),
      queryFn: () => call(route, input) as Promise<RouteResponse<R>>,
    })),
  }) as UseQueryResult<RouteResponse<R>, Error>[];
}

/** 쓰기 계약 → useMutation. 성공하면 조회 캐시를 전부 무효화한다 — v1 규모에서는 이것이 가장 틀리기 어렵다 */
export function useApiMutation<R extends RouteDef>(route: R) {
  const qc = useQueryClient();
  return useMutation<RouteResponse<R>, Error, CallInput<R>>({
    mutationFn: (input) => call(route, input),
    onSuccess: () => qc.invalidateQueries(),
  });
}
